import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ApiState } from '../constants/enums';
import adminServices from '../services/adminServices';
import {
    IDescriptiveTextResponse,
    ICoreTaxes,
    ICoreTaxesResponse,
    ICoreTaxesSummaryResponse,
    IInsuranceClasses,
    IInsuranceClassResponse,
    IInsuranceClassVariationPairsResponse,
    IDescriptiveText,
    ISaveDescriptiveTextResponse,
    ISaveDescriptiveTextRequest,
    ISaveDescriptiveTextResult,
    IUpdateDescriptiveTextResult,
    IVariationsResponse,
    IVariations,
    IError,
    IUpdateResponse,
    ISaveVariationResponse,
    ISaveVariationsRequest,
    ISaveVariationResult,
    IUpdateVariationResult,
    IInsuranceClassesToMap,
    IInsuranceClassVariation,
    IVariationByCountryAndClassParams,
    IVariationByCoreTaxParams,
    IMessageResponse,
    IVariationInput,
    ICoreTaxVariationAssignmentInput,
    IJurisdictions,
    IJurisdictionsResponse,
    ITaxNameResponse,
    ITaxNameCreateResponse,
    ITaxNames,
    ITaxNameCreateRequest,
    ITaxNameUpdateRequest,
    ITaxNameUpdateResponse,
    IDescriptiveTextCreateResponse,
    IDescriptiveTextCreateRequest,
    IVariationMappingReportResponse,
    IVariationUploadedFileResponse,
} from '../types/admin.type';
import { IOptions } from '../../app/components/nds/modal/modalCoreTax';
import _ from 'lodash';

export interface AdminState {
    apiState: {
        getAdminInsuranceClasses: ApiState;
        getInsuranceClassVariationPairs: ApiState;
        getCoreTaxesSummary: ApiState;
        getAdminCoreTaxes: ApiState;
        getDescriptiveText: ApiState;
        postAdminDescriptiveTexts: ApiState;
        patchAdminDescriptiveTexts: ApiState;
        getAdminDescriptiveTextbyId: ApiState;
        getAdminVariations: ApiState;
        saveAdminVariations: ApiState;
        updateAdminVariations: ApiState;
        assignVariationsToInsuranceClasses?: ApiState;
        assignVariationsToCoreTax?: ApiState;
        getVariationByCountryandInsuranceClass?: ApiState;
        getVariationsByCoreTax?: ApiState;
        getJurisdictions?: ApiState;
        postJurisdictions?: ApiState;
        saveLineofBusiness?: ApiState;
        getTaxNames: ApiState;
        createTaxName: ApiState;
        updateTaxName: ApiState;
        createDescriptiveText: ApiState;
        getUploadedVariationMappingReports?: ApiState;
        processVariationMappingFile?: ApiState;
        downloadVariationMappingErrorReport?: ApiState;
    };
    insuranceClasses?: IInsuranceClasses;
    coreTaxes?: ICoreTaxes;
    adminCoreTaxes?: ICoreTaxes;
    descriptiveTexts?: IDescriptiveText;
    variations?: IVariations;
    saveDescriptiveTextRequest?: ISaveDescriptiveTextRequest;
    saveDescriptiveTexts?: ISaveDescriptiveTextResult;
    updateDescriptiveTexts?: IUpdateDescriptiveTextResult;
    saveVariations?: ISaveVariationResult;
    updateVariations?: IUpdateVariationResult;
    variationsbyCountryandClass?: IInsuranceClassVariation;
    variationsbyCoreTax?: IInsuranceClassVariation;
    assignVariationsToInsuranceClasses? : IMessageResponse;
    assignVariationsToCoreTax? : IMessageResponse;
    showModal?: boolean;
    showVariationModel?: boolean;
    isDelete?: boolean;
    descriptiveText: string;
    error: IError;
    descriptiveTextId?: number;
    descriptiveTextType: string;
    descriptiveTextCountry: string;
    variationId?: number;
    variationName?: string;
    variationDescription?: string;
    classesToMap?: IInsuranceClassesToMap[];
    selectedCoreTax?: number;
    selectedLobPair?: number;
    selectedCoreTaxCountry?: string;
    selectedIds?: number[];
    clearModalData?: boolean;
    showVariationEdit?: boolean;
    isCoreTaxNewOrEdit?: boolean;
    isCoreTaxClone?: boolean;
    coreTaxIdNewOrEdit?: number;
    cloneCoreTaxId?: number;
    jurisdictions?: IJurisdictions;
    taxNames?: ITaxNames;
    taxNameId?: number;
    successMessage?: string;
    descriptiveTextIDs?: number[];
    coreTaxTextTaxName: string;
    coreTaxTaxNameObjectToBeAdded: IOptions;
    coreTaxCoInsuranceObjectToBeAdded: IOptions;
    coreTaxTaxPointObjectToBeAdded: IOptions;
    referenceLegislationObjectToBeAdded: IOptions;
    uploadedVariationMappingReports: IVariationMappingReportResponse;
    processVariationMappingFile?: IVariationUploadedFileResponse;
}

export const initialVariation: IInsuranceClassVariation = {
    data: [],
    totalElements: 0,
    totalPages: 0,
    pageNumber: 0,
    pageSize: 10,
    error: {
        errorCode: '',
        errorMessage: '',
        fieldName: '',
    },
};

export const initialVariationMapping: IVariationMappingReportResponse = {
    data: {
        variationMappingReports: [] as any[],
        totalElements: 0,
        totalPages: '',
        pageNumber: 0,
        pageSize: 0,
    },
};

const initialState: AdminState = {
    apiState: {
        getAdminInsuranceClasses: ApiState.NOT_STARTED,
        getInsuranceClassVariationPairs: ApiState.NOT_STARTED,
        getCoreTaxesSummary: ApiState.NOT_STARTED,
        getAdminCoreTaxes: ApiState.NOT_STARTED,
        getDescriptiveText: ApiState.NOT_STARTED,
        postAdminDescriptiveTexts: ApiState.NOT_STARTED,
        patchAdminDescriptiveTexts: ApiState.NOT_STARTED,
        getAdminDescriptiveTextbyId: ApiState.NOT_STARTED,
        getAdminVariations: ApiState.NOT_STARTED,
        saveAdminVariations: ApiState.NOT_STARTED,
        updateAdminVariations: ApiState.NOT_STARTED,
        assignVariationsToInsuranceClasses: ApiState.NOT_STARTED,
        assignVariationsToCoreTax: ApiState.NOT_STARTED,
        getVariationByCountryandInsuranceClass: ApiState.NOT_STARTED,
        getVariationsByCoreTax: ApiState.NOT_STARTED,
        getJurisdictions: ApiState.NOT_STARTED,
        postJurisdictions: ApiState.NOT_STARTED,
        saveLineofBusiness: ApiState.NOT_STARTED,
        getTaxNames: ApiState.NOT_STARTED,
        createTaxName: ApiState.NOT_STARTED,
        updateTaxName: ApiState.NOT_STARTED,
        createDescriptiveText: ApiState.NOT_STARTED,
        getUploadedVariationMappingReports: ApiState.NOT_STARTED,
        processVariationMappingFile: ApiState.NOT_STARTED,
        downloadVariationMappingErrorReport: ApiState.NOT_STARTED,
    },
    insuranceClasses: {
        data: [],
        totalElements: 0,
        totalPages: 0,
        pageNumber: 0,
        pageSize: 10,
    },
    taxNames: {
        data: [],
        totalElements: 0,
        totalPages: 0,
        pageNumber: 0,
        pageSize: 10,
    },
    coreTaxes: {
        data: [],
        totalElements: 0,
        totalPages: 0,
        pageNumber: 0,
        pageSize: 10,
        searchString: '',
    },
    adminCoreTaxes: {
        data: [],
        totalElements: 0,
        totalPages: 0,
        pageNumber: 0,
        pageSize: 10,
        searchString: '',
    },
    descriptiveTexts: {
        data: [],
        totalElements: 0,
        totalPages: 0,
        pageNumber: 0,
        pageSize: 10,
        searchString: '',
    },
    variations: {
        data: [],
        totalElements: 0,
        totalPages: 0,
        pageNumber: 0,
        pageSize: 10,
        searchString: '',
    },
    saveDescriptiveTexts: {
        saveData: { descriptiveTextId: [] },
    },
    updateDescriptiveTexts: {
        updateData: { message: '' },
    },
    saveVariations: {
        saveData: { variationIds: [] },
    },
    updateVariations: {
        updateData: { message: '' },
    },
    assignVariationsToInsuranceClasses: {
        responseData : {message: ''},
    },
    assignVariationsToCoreTax: {
        responseData : {message: ''},
    },
    variationsbyCountryandClass: initialVariation,
    variationsbyCoreTax: initialVariation,
    showModal: false,
    showVariationModel: false,
    isDelete: false,
    descriptiveText: '',
    descriptiveTextId: 0,
    descriptiveTextType: '0',
    descriptiveTextCountry: '',
    variationId: 0,
    variationName: '',
    variationDescription: '',
    selectedCoreTax: 0,
    selectedLobPair: 0,
    selectedCoreTaxCountry: '',
    selectedIds: [],
    clearModalData: false,
    error: {
        errorCode: '',
        errorMessage: '',
    },
    classesToMap: [],
    showVariationEdit: false,
    isCoreTaxNewOrEdit: false,
    isCoreTaxClone: false,
    coreTaxIdNewOrEdit: 0,
    cloneCoreTaxId: 0,
    jurisdictions: {
        data: [],
        totalElements: 0,
        totalPages: 0,
        pageNumber: 0,
        pageSize: 10,
    },
    taxNameId: 0,
    successMessage: '',
    descriptiveTextIDs: [],
    coreTaxTextTaxName: '',
    coreTaxTaxNameObjectToBeAdded: {value: '', label: '', disabled: false},
    coreTaxCoInsuranceObjectToBeAdded: {value: '', label: '', disabled: false},
    coreTaxTaxPointObjectToBeAdded: {value: '', label: '', disabled: false},
    referenceLegislationObjectToBeAdded: {value: '', label: '', disabled: false},
    uploadedVariationMappingReports: initialVariationMapping,
}

export interface DescriptiveTextPayload {
    code: string;
}

export interface DescriptiveTextIdPayload {
    code: number;
}

export interface DescriptiveTextTypePayload {
    code: string;
}

export const getAdminInsuranceClasses = createAsyncThunk<
    IInsuranceClassResponse,
    {
        pageSize?: number;
        pageNumber?: number;
        fieldName?: string;
        sortOrder?: string;
    }
>('AdminPageSlice/getAdminInsuranceClasses', async ({ pageSize, pageNumber, fieldName, sortOrder }) => {
    const response = await adminServices.getAdminInsuranceClasses({
        pageSize: pageSize !== undefined ? String(pageSize) : undefined,
        pageNumber: pageNumber !== undefined ? String(pageNumber) : undefined,
        fieldName,
        sortOrder,
    });
    return {
        ...response,
        data: {
            ...response.data,
            pageSize: pageSize !== undefined ? +pageSize : 10,
        },
    };
});

export const getInsuranceClassVariationPairs = createAsyncThunk<
    IInsuranceClassVariationPairsResponse,
    {
        pageSize?: number;
        pageNumber?: number;
        fieldName?: string;
        sortOrder?: string;
        searchString?: string;
    }
>('AdminPageSlice/getAdminInsuranceClasses', async ({ pageSize, pageNumber, fieldName, sortOrder, searchString }) => {
    const response = await adminServices.getInsuranceClassVariationPairs({
        pageSize: pageSize !== undefined ? String(pageSize) : undefined,
        pageNumber: pageNumber !== undefined ? String(pageNumber) : undefined,
        fieldName,
        sortOrder,
        searchString,
    });
    return {
        ...response,
        data: {
            ...response.data,
            pageSize: pageSize !== undefined ? +pageSize : 10,
        },
    };
});

export const getCoreTaxesSummary = createAsyncThunk<
    ICoreTaxesSummaryResponse,
    {
        pageSize?: number;
        pageNumber?: number;
        fieldName?: string;
        sortOrder?: string;
        searchString?: string;
    }
>('AdminPageSlice/getCoreTaxesSummary', async ({ pageSize, pageNumber, fieldName, sortOrder, searchString }) => {
    const response = await adminServices.getCoreTaxesSummary({
        pageSize: pageSize !== undefined ? String(pageSize) : undefined,
        pageNumber: pageNumber !== undefined ? String(pageNumber) : undefined,
        fieldName,
        sortOrder,
        searchString,
    });
    return {
        ...response,
        data: {
            ...response.data,
            pageSize: pageSize !== undefined ? +pageSize : 10,
        },
    };
});

export const getAdminCoreTaxes = createAsyncThunk<
    ICoreTaxesResponse,
    {
        pageSize?: number;
        pageNumber?: number;
        fieldName?: string;
        sortOrder?: string;
        searchString?: string;
    }
>('AdminPageSlice/getAdminCoreTaxes', async ({ pageSize, pageNumber, fieldName, sortOrder, searchString }) => {
    const response = await adminServices.getAdminCoreTaxes({
        pageSize: pageSize !== undefined ? String(pageSize) : undefined,
        pageNumber: pageNumber !== undefined ? String(pageNumber) : undefined,
        fieldName,
        sortOrder,
        searchString,
    });
    return {
        ...response,
        data: {
            ...response.data,
            pageSize: pageSize !== undefined ? +pageSize : 10,
        },
    };
});

export const getDescriptiveText = createAsyncThunk<
    IDescriptiveTextResponse,
    {
        pageSize?: number;
        pageNumber?: number;
        fieldName?: string;
        sortOrder?: string;
        searchString?: string;
    }
>('AdminPageSlice/getDescriptiveText', async ({ pageSize, pageNumber, fieldName, sortOrder, searchString }) => {
    const response = await adminServices.getDescriptiveText({
        pageSize: pageSize !== undefined ? String(pageSize) : undefined,
        pageNumber: pageNumber !== undefined ? String(pageNumber) : undefined,
        fieldName,
        sortOrder,
        searchString,
    });
    return {
        ...response,
        data: {
            ...response.data,
            pageSize: pageSize !== undefined ? +pageSize : 10,
        },
    };
});

export const postAdminDescriptiveTexts = createAsyncThunk<
    ISaveDescriptiveTextResponse,
    {
        descriptiveTextType: string;
        descriptiveText: string;
    }
>('AdminPageSlice/postAdminDescriptiveTexts', async ({ descriptiveTextType, descriptiveText }) => {
    const response = await adminServices.saveAdminDescriptiveTexts({
        descriptiveTexts: [
            {
                descriptiveTextType,
                descriptiveText,
            },
        ],
    });
    return response;
});

export const patchAdminDescriptiveTexts = createAsyncThunk<
    IUpdateResponse,
    {
        descriptiveTexts: {
            descriptiveTextId: number;
            descriptiveTextType: string;
            descriptiveText: string;
            isDeleted: boolean;
        }[];
    }
>('AdminPageSlice/patchAdminDescriptiveTexts', async (descriptiveTexts) => {
    const response = await adminServices.updateAdminDescriptiveTexts(descriptiveTexts);
    return response;
});

export const getAdminVariations = createAsyncThunk<
    IVariationsResponse,
    {
        pageSize?: number;
        pageNumber?: number;
        fieldName?: string;
        sortOrder?: string;
        searchString?: string;
    }
>('AdminPageSlice/getVariations', async ({ pageSize, pageNumber, fieldName, sortOrder, searchString }) => {
    const response = await adminServices.getVariations({
        pageSize: pageSize !== undefined ? String(pageSize) : undefined,
        pageNumber: pageNumber !== undefined ? String(pageNumber) : undefined,
        fieldName,
        sortOrder,
        searchString,
    });
    return {
        ...response,
        data: {
            ...response.data,
            pageSize: pageSize !== undefined ? +pageSize : 10,
        },
    };
});

export const saveAdminVariations = createAsyncThunk<ISaveVariationResponse, { name: string; description: string }>(
    'AdminPageSlice/saveAdminVariations',
    async ({ name, description }) => {
        const response = await adminServices.saveAdminVariations({
            variations: [
                {
                    name,
                    description,
                },
            ],
        });
        return response;
    },
);

export const updateAdminVariations = createAsyncThunk<
    IUpdateResponse,
    {
        variations: ISaveVariationsRequest[];
    }
>('AdminPageSlice/updateAdminVariations', async (variations) => {
    const response = await adminServices.updateAdminVariations(variations);
    return response;
});

export const getVariationByCountryandInsuranceClass = createAsyncThunk<
    IInsuranceClassVariation,
    {
        inputParameters: IVariationByCountryAndClassParams;
    }
>('AdminPageSlice/getVariationByCountryandInsuranceClass', async (inputParameters) => {
    const response = await adminServices.getVariationByCountryandInsuranceClass(inputParameters);
    return response;
});

export const getVariationsByCoreTax = createAsyncThunk<
    IInsuranceClassVariation,
    {
        inputParameters: IVariationByCoreTaxParams;
    }
>('AdminPageSlice/getVariationsByCoreTax', async (inputParameters) => {
    const response = await adminServices.getVariationsByCoreTax(inputParameters);
    return response;
});

export const assignVariationsToInsuranceClasses = createAsyncThunk<
    IUpdateResponse,
    {
        inputParameters: IVariationInput[];
    }
>('AdminPageSlice/assignVariationsToInsuranceClasses', async (inputParameters) => {
    const response = await adminServices.assignVariationsToInsuranceClasses(inputParameters);
    return response;
});

export const assignVariationsToCoreTax = createAsyncThunk<
    IUpdateResponse,
    {
        inputParameters: ICoreTaxVariationAssignmentInput;
    }
>('AdminPageSlice/assignVariationsToCoreTax', async (inputParameters) => {
    const response = await adminServices.assignVariationsToCoreTax(inputParameters);
    return response;
});

export const getJurisdictions = createAsyncThunk<
    IJurisdictionsResponse,
    {
        pageSize?: number;
        pageNumber?: number;
        fieldName?: string;
        sortOrder?: string;
        searchString?: string;
    }
>('AdminPageSlice/getJurisdictions', async ({ pageSize, pageNumber, fieldName, sortOrder, searchString }) => {
    const response = await adminServices.getJurisdictions({
        pageSize: pageSize !== undefined ? String(pageSize) : undefined,
        pageNumber: pageNumber !== undefined ? String(pageNumber) : undefined,
        fieldName,
        sortOrder,
        searchString,
    });
    return {
        ...response,
        data: {
            ...response.data,
            pageSize: pageSize !== undefined ? +pageSize : 10,
        },
    };
});

export const getUploadedVariationMappingReports = createAsyncThunk<
IVariationMappingReportResponse,
    {
        pageSize?: number;
        pageNumber?: number;
        fieldName?: string;
        sortOrder?: string;
        searchString?: string;
    }
>('AdminPageSlice/getUploadedVariationMappingReports', async ({ pageSize, pageNumber, fieldName, sortOrder, searchString }) => {
    const response = await adminServices.getUploadedVariationMappingReports({
        pageSize: pageSize !== undefined ? pageSize : undefined,
        pageNumber: pageNumber !== undefined ? pageNumber : undefined,
        fieldName,
        sortOrder,
        searchString,
    });
    return {
        ...response,
        data: {
            ...response.data,
            pageSize: pageSize !== undefined ? +pageSize : 10,
        },
    };
});

export const getTaxNames = createAsyncThunk<
    ITaxNameResponse,
    {
        pageSize?: number;
        pageNumber?: number;
        fieldName?: string;
        sortOrder?: string;
    }
>('AdminPageSlice/getTaxNames', async ({ pageSize, pageNumber, fieldName, sortOrder }) => {
    const response = await adminServices.getTaxNames({
        pageSize: pageSize !== undefined ? String(pageSize) : undefined,
        pageNumber: pageNumber !== undefined ? String(pageNumber) : undefined,
        fieldName,
        sortOrder,
    });
    return {
        ...response,
        data: {
            ...response.data,
            pageSize: pageSize !== undefined ? +pageSize : 10,
        },
    };
});

export const createTaxName = createAsyncThunk<ITaxNameCreateResponse, ITaxNameCreateRequest[]>(
    'AdminPageSlice/createTaxName',
    async (taxNamePayload: ITaxNameCreateRequest[]) => {
        return adminServices.createTaxName(taxNamePayload).then((res) => res);
    },
);

export const updateTaxName = createAsyncThunk<ITaxNameUpdateResponse, ITaxNameUpdateRequest[]>(
    'AdminPageSlice/updateTaxName',
    async (taxNamePayload: ITaxNameUpdateRequest[]) => {
        return adminServices.updateTaxName(taxNamePayload).then((res) => res);
    },
);

export const createDescriptiveText = createAsyncThunk<IDescriptiveTextCreateResponse, IDescriptiveTextCreateRequest[]>(
    'AdminPageSlice/createDescriptiveText',
    async (descriptiveTextPayload: IDescriptiveTextCreateRequest[]) => {
        return adminServices.createDescriptiveText(descriptiveTextPayload).then((res) => res);
    },
);

export const processVariationMappingFile = createAsyncThunk<any, File>(
    'AdminPageSlice/processVariationMappingFile',
    async (file: File) => {
        return adminServices.processVariationMappingFile(file).then((res) => res);
    },
);

const adminSlice = createSlice({
    name: 'AdminPage',
    initialState,
    extraReducers(builder) {
        builder
            .addCase(getAdminInsuranceClasses.pending, (state) => {
                state.apiState.getAdminInsuranceClasses = ApiState.PENDING;
            })
            .addCase(getAdminInsuranceClasses.fulfilled, (state, action: PayloadAction<IInsuranceClassResponse>) => {
                if (state.insuranceClasses) {
                    state.insuranceClasses.data = action.payload.data?.insuranceClasses || [];
                    state.insuranceClasses.totalElements = action.payload.data?.totalElements || 0;
                    state.insuranceClasses.totalPages = action.payload.data?.totalPages || 0;
                    state.insuranceClasses.pageNumber = action.payload.data?.pageNumber || 0;
                    state.insuranceClasses.pageSize = action.payload.data?.pageSize || 0;
                }
                state.apiState.getAdminInsuranceClasses = ApiState.FULFILLED;
            })
            .addCase(getAdminInsuranceClasses.rejected, (state) => {
                state.apiState.getAdminInsuranceClasses = ApiState.REJECTED;
            })
            .addCase(getCoreTaxesSummary.pending, (state) => {
                state.apiState.getCoreTaxesSummary = ApiState.PENDING;
            })
            .addCase(getCoreTaxesSummary.fulfilled, (state, action: PayloadAction<ICoreTaxesSummaryResponse>) => {
                if (state.coreTaxes) {
                    state.coreTaxes.data = action.payload.data?.coreTaxes || [];
                    state.coreTaxes.totalElements = action.payload.data?.totalElements || 0;
                    state.coreTaxes.totalPages = action.payload.data?.totalPages || 0;
                    state.coreTaxes.pageNumber = action.payload.data?.pageNumber || 0;
                    state.coreTaxes.pageSize = action.payload.data?.pageSize || 0;
                    state.coreTaxes.searchString = action.payload.data?.searchString || '';
                }
                state.apiState.getCoreTaxesSummary = ApiState.FULFILLED;
            })
            .addCase(getCoreTaxesSummary.rejected, (state) => {
                state.apiState.getCoreTaxesSummary = ApiState.REJECTED;
            })
            .addCase(getAdminCoreTaxes.pending, (state) => {
                state.apiState.getAdminCoreTaxes = ApiState.PENDING;
            })
            .addCase(getAdminCoreTaxes.fulfilled, (state, action: PayloadAction<ICoreTaxesResponse>) => {
                if (state.adminCoreTaxes) {
                    state.adminCoreTaxes.data = action.payload.data?.coreTaxes || [];
                    state.adminCoreTaxes.totalElements = action.payload.data?.totalElements || 0;
                    state.adminCoreTaxes.totalPages = action.payload.data?.totalPages || 0;
                    state.adminCoreTaxes.pageNumber = action.payload.data?.pageNumber || 0;
                    state.adminCoreTaxes.pageSize = action.payload.data?.pageSize || 0;
                    state.adminCoreTaxes.searchString = action.payload.data?.searchString || '';
                }
                state.apiState.getAdminCoreTaxes = ApiState.FULFILLED;
            })
            .addCase(getAdminCoreTaxes.rejected, (state) => {
                state.apiState.getAdminCoreTaxes = ApiState.REJECTED;
            })
            .addCase(getDescriptiveText.pending, (state) => {
                state.apiState.getDescriptiveText = ApiState.PENDING;
            })
            .addCase(getDescriptiveText.fulfilled, (state, action: PayloadAction<IDescriptiveTextResponse>) => {
                if (state.descriptiveTexts) {
                    state.descriptiveTexts.data = action.payload.data?.descriptiveTexts || [];
                    state.descriptiveTexts.totalElements = action.payload.data?.totalElements || 0;
                    state.descriptiveTexts.totalPages = action.payload.data?.totalPages || 0;
                    state.descriptiveTexts.pageNumber = action.payload.data?.pageNumber || 0;
                    state.descriptiveTexts.pageSize = action.payload.data?.pageSize || 0;
                }
                state.apiState.getDescriptiveText = ApiState.FULFILLED;
            })
            .addCase(getDescriptiveText.rejected, (state) => {
                state.apiState.getDescriptiveText = ApiState.REJECTED;
            })
            .addCase(getAdminVariations.pending, (state) => {
                state.apiState.getAdminVariations = ApiState.PENDING;
            })
            .addCase(getAdminVariations.fulfilled, (state, action: PayloadAction<IVariationsResponse>) => {
                if (state.variations) {
                    state.variations.data = action.payload.data?.variations || [];
                    state.variations.totalElements = action.payload.data?.totalElements || 0;
                    state.variations.totalPages = action.payload.data?.totalPages || 0;
                    state.variations.pageNumber = action.payload.data?.pageNumber || 0;
                    state.variations.pageSize = action.payload.data?.pageSize || 0;
                }
                state.apiState.getAdminVariations = ApiState.FULFILLED;
            })
            .addCase(getAdminVariations.rejected, (state) => {
                state.apiState.getAdminVariations = ApiState.REJECTED;
            })
            .addCase(getJurisdictions.pending, (state) => {
                state.apiState.getJurisdictions = ApiState.PENDING;
            })
            .addCase(getJurisdictions.fulfilled, (state, action: PayloadAction<IJurisdictionsResponse>) => {
                if (state.jurisdictions) {
                    state.jurisdictions.data = action.payload.data?.jurisdictions || [];
                    state.jurisdictions.totalElements = action.payload.data?.totalElements || 0;
                    state.jurisdictions.totalPages = action.payload.data?.totalPages || 0;
                    state.jurisdictions.pageNumber = action.payload.data?.pageNumber || 0;
                    state.jurisdictions.pageSize = action.payload.data?.pageSize || 0;
                }
                state.apiState.getJurisdictions = ApiState.FULFILLED;
            })
            .addCase(getJurisdictions.rejected, (state) => {
                state.apiState.getJurisdictions = ApiState.REJECTED;
            })
            .addCase(postAdminDescriptiveTexts.pending, (state) => {
                state.apiState.postAdminDescriptiveTexts = ApiState.PENDING;
            })
            .addCase(
                postAdminDescriptiveTexts.fulfilled,
                (state, action: PayloadAction<ISaveDescriptiveTextResponse>) => {
                    if (action.payload.errorCode && action.payload.errorMessage) {
                        if (state.error) {
                            state.error.errorCode = action.payload.errorCode;
                            state.error.errorMessage = action.payload.errorMessage;
                            state.apiState.postAdminDescriptiveTexts = ApiState.REJECTED;
                        }
                    } else {
                        if (state.saveDescriptiveTexts) {
                            state.saveDescriptiveTexts.saveData.descriptiveTextId =
                                action.payload.data?.descriptiveTextId || [];
                        }
                        state.apiState.postAdminDescriptiveTexts = ApiState.FULFILLED;
                    }
                },
            )
            .addCase(postAdminDescriptiveTexts.rejected, (state) => {
                state.apiState.postAdminDescriptiveTexts = ApiState.REJECTED;
            })
            .addCase(patchAdminDescriptiveTexts.pending, (state) => {
                state.apiState.patchAdminDescriptiveTexts = ApiState.PENDING;
            })
            .addCase(patchAdminDescriptiveTexts.fulfilled, (state, action: PayloadAction<IUpdateResponse>) => {
                if (state.updateDescriptiveTexts) {
                    state.updateDescriptiveTexts.updateData.message = action.payload.data?.message || '';
                }
                state.apiState.patchAdminDescriptiveTexts = ApiState.FULFILLED;
            })
            .addCase(patchAdminDescriptiveTexts.rejected, (state) => {
                state.apiState.patchAdminDescriptiveTexts = ApiState.REJECTED;
            })
            //variation
            .addCase(saveAdminVariations.pending, (state) => {
                state.apiState.saveAdminVariations = ApiState.PENDING;
            })
            .addCase(saveAdminVariations.fulfilled, (state, action: PayloadAction<ISaveVariationResponse>) => {
                if (action.payload.errorCode && action.payload.errorMessage) {
                    if (state.error) {
                        state.error.errorCode = action.payload.errorCode;
                        state.error.errorMessage = action.payload.errorMessage;
                        state.apiState.saveAdminVariations = ApiState.REJECTED;
                    }
                } else {
                    if (state.saveVariations) {
                        state.saveVariations.saveData.variationIds = action.payload.data?.variationIds || [];
                    }
                    state.apiState.saveAdminVariations = ApiState.FULFILLED;
                }
            })
            .addCase(saveAdminVariations.rejected, (state) => {
                state.apiState.saveAdminVariations = ApiState.REJECTED;
            })
            //variationbycountryandclass
            .addCase(getVariationByCountryandInsuranceClass.pending, (state) => {
                state.apiState.getVariationByCountryandInsuranceClass = ApiState.PENDING;
            })
            .addCase(getVariationByCountryandInsuranceClass.fulfilled, (state, action: PayloadAction<IInsuranceClassVariation>) => {
                if (action.payload.error) {
                    state.variationsbyCountryandClass = initialVariation;
                    if (state.error) {
                        state.error.errorCode = action.payload.error.errorCode || '';
                        state.error.errorMessage = action.payload.error.errorMessage || '';
                        state.apiState.getVariationByCountryandInsuranceClass = ApiState.REJECTED;
                    }
                } else {
                    if (state.variationsbyCountryandClass) {
                        state.variationsbyCountryandClass.data = action.payload.data || [];
                        state.variationsbyCountryandClass.totalElements = action.payload.totalElements || 0;
                        state.variationsbyCountryandClass.totalPages = action.payload.totalPages || 0;
                        state.variationsbyCountryandClass.pageNumber = action.payload.pageNumber || 0;
                        state.variationsbyCountryandClass.pageSize = action.payload.pageSize || 10;
                    }
                    state.apiState.getVariationByCountryandInsuranceClass = ApiState.FULFILLED;
                }
            })
            .addCase(getVariationByCountryandInsuranceClass.rejected, (state) => {
                state.apiState.getVariationByCountryandInsuranceClass = ApiState.REJECTED;
            })
            //variationsbycoretax
            .addCase(getVariationsByCoreTax.pending, (state) => {
                state.apiState.getVariationsByCoreTax = ApiState.PENDING;
            })
            .addCase(getVariationsByCoreTax.fulfilled, (state, action: PayloadAction<IInsuranceClassVariation>) => {
                if (action.payload.error) {
                    state.variationsbyCoreTax = initialVariation;
                    if (state.error) {
                        state.error.errorCode = action.payload.error.errorCode || '';
                        state.error.errorMessage = action.payload.error.errorMessage || '';
                        state.apiState.getVariationsByCoreTax = ApiState.REJECTED;
                    }
                } else {
                    if (state.variationsbyCoreTax) {
                        state.variationsbyCoreTax.data = action.payload.data || [];
                        state.variationsbyCoreTax.totalElements = action.payload.totalElements || 0;
                        state.variationsbyCoreTax.totalPages = action.payload.totalPages || 0;
                        state.variationsbyCoreTax.pageNumber = action.payload.pageNumber || 0;
                        state.variationsbyCoreTax.pageSize = action.payload.pageSize || 10;
                    }
                    state.apiState.getVariationsByCoreTax = ApiState.FULFILLED;
                }
            })
            .addCase(getVariationsByCoreTax.rejected, (state) => {
                state.apiState.getVariationsByCoreTax = ApiState.REJECTED;
            })
            .addCase(updateAdminVariations.pending, (state) => {
                state.apiState.updateAdminVariations = ApiState.PENDING;
            })
            .addCase(updateAdminVariations.fulfilled, (state, action: PayloadAction<IUpdateResponse>) => {
                if (state.updateVariations) {
                    state.updateVariations.updateData.message = action.payload.data?.message || '';
                }
                state.apiState.updateAdminVariations = ApiState.FULFILLED;
            })
            .addCase(updateAdminVariations.rejected, (state) => {
                state.apiState.updateAdminVariations = ApiState.REJECTED;
            })
            //assignVariationsToInsuranceClasses
            .addCase(assignVariationsToInsuranceClasses.pending, (state) => {
                state.apiState.assignVariationsToInsuranceClasses = ApiState.PENDING;
            })
            .addCase(assignVariationsToInsuranceClasses.fulfilled, (state, action: PayloadAction<IUpdateResponse>) => {
                if (action.payload.error) {
                    state.variationsbyCoreTax = initialVariation;
                    if (state.error) {
                        state.error.errorCode = action.payload.error.errorCode || '';
                        state.error.errorMessage = action.payload.error.errorMessage || '';
                        state.apiState.assignVariationsToInsuranceClasses = ApiState.REJECTED;
                    }
                } else if (state.assignVariationsToInsuranceClasses) {
                    state.assignVariationsToInsuranceClasses.responseData.message = action.payload.data?.message || '';
                state.apiState.assignVariationsToInsuranceClasses = ApiState.FULFILLED;
                }
                
            })
            .addCase(assignVariationsToInsuranceClasses.rejected, (state) => {
                state.apiState.assignVariationsToInsuranceClasses = ApiState.REJECTED;
            })
            //assignVariationsToCoreTax
            .addCase(assignVariationsToCoreTax.pending, (state) => {
                state.apiState.assignVariationsToCoreTax = ApiState.PENDING;
            })
            .addCase(assignVariationsToCoreTax.fulfilled, (state, action: PayloadAction<IUpdateResponse>) => {
                if(action.payload.error) {
                    if(state.error) {
                        state.error.errorCode = action.payload.error.errorCode || '';
                        state.error.errorMessage = action.payload.error.errorMessage || '';
                        state.apiState.assignVariationsToCoreTax = ApiState.REJECTED;
                    }
                } else if (state.assignVariationsToCoreTax) {
                    state.assignVariationsToCoreTax.responseData.message = action.payload.data?.message || '';
                    state.apiState.assignVariationsToCoreTax = ApiState.FULFILLED;
                }                
            })
            .addCase(assignVariationsToCoreTax.rejected, (state) => {
                state.apiState.assignVariationsToCoreTax = ApiState.REJECTED;
                })
            .addCase(getTaxNames.pending, (state) => {
                state.apiState.getTaxNames = ApiState.PENDING;
            })
            .addCase(getTaxNames.fulfilled, (state, action: PayloadAction<ITaxNameResponse>) => {
                if (state.taxNames) {
                    state.taxNames.data = action.payload.data?.taxNames || [];
                    state.taxNames.totalElements = action.payload.data?.totalElements || 0;
                    state.taxNames.totalPages = action.payload.data?.totalPages || 0;
                    state.taxNames.pageNumber = action.payload.data?.pageNumber || 0;
                    state.taxNames.pageSize = action.payload.data?.pageSize || 0;
                }
                state.apiState.getTaxNames = ApiState.FULFILLED;
            })
            .addCase(getTaxNames.rejected, (state) => {
                state.apiState.getTaxNames = ApiState.REJECTED;
            })
            .addCase(createTaxName.pending, (state) => {
                state.apiState.createTaxName = ApiState.PENDING;
            })
            .addCase(createTaxName.fulfilled, (state, action: PayloadAction<ITaxNameCreateResponse>) => {
                state.taxNameId = action.payload.data?.taxNameId[0];
                state.apiState.createTaxName = ApiState.FULFILLED;
            })
            .addCase(createTaxName.rejected, (state) => {
                state.apiState.createTaxName = ApiState.REJECTED;
            })
            .addCase(updateTaxName.pending, (state) => {
                state.apiState.updateTaxName = ApiState.PENDING;
            })
            .addCase(updateTaxName.fulfilled, (state, action: PayloadAction<ITaxNameUpdateResponse>) => {
                state.successMessage = action.payload.data?.message;
                state.apiState.updateTaxName = ApiState.FULFILLED;
            })
            .addCase(updateTaxName.rejected, (state) => {
                state.apiState.updateTaxName = ApiState.REJECTED;
            })
            .addCase(createDescriptiveText.pending, (state) => {
                state.apiState.createDescriptiveText = ApiState.PENDING;
            })
            .addCase(
                createDescriptiveText.fulfilled,
                (state, action: PayloadAction<IDescriptiveTextCreateResponse>) => {
                    state.descriptiveTextIDs = action.payload.data?.descriptiveTextId;
                    state.apiState.createDescriptiveText = ApiState.FULFILLED;
                },
            )
            .addCase(createDescriptiveText.rejected, (state) => {
                state.apiState.createDescriptiveText = ApiState.REJECTED;
            })
            .addCase(getUploadedVariationMappingReports.pending, (state) => {
                state.apiState.getUploadedVariationMappingReports = ApiState.PENDING;
            })
            .addCase(getUploadedVariationMappingReports.fulfilled, (state, action: PayloadAction<IVariationMappingReportResponse>) => {
                state.uploadedVariationMappingReports.data.variationMappingReports = action.payload.data?.variationMappingReports || [];
                state.uploadedVariationMappingReports.data.totalElements = action.payload.data?.totalElements || 0;
                state.uploadedVariationMappingReports.data.totalPages = action.payload.data?.totalPages || '';
                state.uploadedVariationMappingReports.data.pageNumber = action.payload.data?.pageNumber || 0;
                state.uploadedVariationMappingReports.data.pageSize = action.payload.data?.pageSize || 0;
                state.apiState.getUploadedVariationMappingReports = ApiState.FULFILLED;
            })
            .addCase(getUploadedVariationMappingReports.rejected, (state) => {
                state.apiState.getUploadedVariationMappingReports = ApiState.REJECTED;
            })
            .addCase(processVariationMappingFile.pending, (state) => {
                state.apiState.processVariationMappingFile = ApiState.PENDING;
            })
            .addCase(processVariationMappingFile.fulfilled, (state, action: PayloadAction<IVariationUploadedFileResponse>) => {
                state.processVariationMappingFile = {
                    errorFileId: action.payload.errorFileId || '',
                    errorFileName: action.payload.errorFileName || '',
                    uploadedFileId: action.payload.uploadedFileId || '',
                    uploadedFileName: action.payload.uploadedFileName || '',
                    processingStatus: action.payload.processingStatus || '',
                    uploadedBy: action.payload.uploadedBy || '',
                    uploadedDate: action.payload.uploadedDate || '',
                };
                state.apiState.processVariationMappingFile = ApiState.FULFILLED;
            })
            .addCase(processVariationMappingFile.rejected, (state) => {
                state.apiState.processVariationMappingFile = ApiState.REJECTED;
            });
    },
    reducers: {
        setDescriptiveText(state, action: PayloadAction<string>) {
            state.descriptiveText = action.payload;
        },
        setDescriptiveTextId(state, action: PayloadAction<number>) {
            state.descriptiveTextId = action.payload;
        },
        setDescriptiveTextType(state, action: PayloadAction<string>) {
            state.descriptiveTextType = action.payload;
        },
        setDescriptiveTextCountry(state, action: PayloadAction<string>) {
            state.descriptiveTextCountry = action.payload;
        },
        setShowModal(state, action: PayloadAction<boolean>) {
            state.showModal = action.payload;
        },
        setShowVariationModel(state, action: PayloadAction<boolean>) {
            state.showVariationModel = action.payload;
        },
        setIsDelete(state, action: PayloadAction<boolean>) {
            state.isDelete = action.payload;
        },
        setSelectedIds(state, action: PayloadAction<number[]>) {
            state.selectedIds = action.payload;
        },
        setClearModalData(state, action: PayloadAction<boolean>) {
            state.clearModalData = action.payload;
        },
        setVariationName(state, action: PayloadAction<string>) {
            state.variationName = action.payload;
        },
        setVariationId(state, action: PayloadAction<number>) {
            state.variationId = action.payload;
        },
        setVariationDescription(state, action: PayloadAction<string>) {
            state.variationDescription = action.payload;
        },
        setCoreTaxId(state, action: PayloadAction<number>) {
            state.selectedCoreTax = action.payload;
        },
        setLobPairId(state, action: PayloadAction<number>) {
            state.selectedLobPair = action.payload;
        },
        setCoreTaxCountry(state, action: PayloadAction<string>) {
            state.selectedCoreTaxCountry = action.payload;
        },
        setClassesToMap(state, action: PayloadAction<IInsuranceClassesToMap[]>) {
            state.classesToMap = action.payload;
        },
        setVariationEdit(state, action: PayloadAction<boolean>) {
            state.showVariationEdit = action.payload;
        },
        setCoreTaxNewOrEdit(state, action: PayloadAction<boolean>) {
            state.isCoreTaxNewOrEdit = action.payload;
        },
        setCoreTaxClone(state, action: PayloadAction<boolean>) {
            state.isCoreTaxClone = action.payload;
        },
        setNewOrEditCoreTaxId(state, action: PayloadAction<number>) {
            state.coreTaxIdNewOrEdit = action.payload;
        },
        setCloneCoreTaxId(state, action: PayloadAction<number>) {
            state.cloneCoreTaxId = action.payload;
            },
        setCoreTaxTextTaxName(state, action: PayloadAction<string>) {
            state.coreTaxTextTaxName = action.payload;
        },
        setCoreTaxTaxNameObjectToBeAdded(state, action: PayloadAction<IOptions>) {
            state.coreTaxTaxNameObjectToBeAdded = action.payload;
        },
        resetCoreTaxTaxNameObjectToBeAdded(state) {
            var cloneTaxNameObject = _.cloneDeep(state.coreTaxTaxNameObjectToBeAdded);
            cloneTaxNameObject.label = '';
            cloneTaxNameObject.value = '';
            state.coreTaxTaxNameObjectToBeAdded = cloneTaxNameObject;
        },
        setCoreTaxCoInsuranceObjectToBeAdded(state, action: PayloadAction<IOptions>) {
            state.coreTaxCoInsuranceObjectToBeAdded = action.payload;
        },
        resetCoreTaxCoInsuranceObjectToBeAdded(state) {
            var cloneCoInsuranceObject = _.cloneDeep(state.coreTaxCoInsuranceObjectToBeAdded);
            cloneCoInsuranceObject.label = '';
            cloneCoInsuranceObject.value = '';
            state.coreTaxCoInsuranceObjectToBeAdded = cloneCoInsuranceObject;
        },
        setCoreTaxTaxPointObjectToBeAdded(state, action: PayloadAction<IOptions>) {
            state.coreTaxTaxPointObjectToBeAdded = action.payload;
        },
        resetCoreTaxTaxPointObjectToBeAdded(state) {
            var cloneTaxPointObject = _.cloneDeep(state.coreTaxTaxPointObjectToBeAdded);
            cloneTaxPointObject.label = '';
            cloneTaxPointObject.value = '';
            state.coreTaxTaxPointObjectToBeAdded = cloneTaxPointObject;
        },
        setCoreTaxReferenceLegislationObjectToBeAdded(state, action: PayloadAction<IOptions>) {
            state.referenceLegislationObjectToBeAdded = action.payload;
        },
        resetCoreTaxReferenceLegislationObjectToBeAdded(state) {
            var cloneReferenceLegislationObject = _.cloneDeep(state.referenceLegislationObjectToBeAdded);
            cloneReferenceLegislationObject.label = '';
            cloneReferenceLegislationObject.value = '';
            state.referenceLegislationObjectToBeAdded = cloneReferenceLegislationObject;
        },
        resetDescriptiveTextIDs(state){
            state.descriptiveTextIDs = [];
            state.coreTaxTextTaxName = '';
            state.taxNameId = 0;
        },        
        resetApiState(state) {
            state.apiState = {
                getAdminInsuranceClasses: ApiState.NOT_STARTED,
                getInsuranceClassVariationPairs: ApiState.NOT_STARTED,
                getCoreTaxesSummary: ApiState.NOT_STARTED,
                getAdminCoreTaxes: ApiState.NOT_STARTED,
                getDescriptiveText: ApiState.NOT_STARTED,
                postAdminDescriptiveTexts: ApiState.NOT_STARTED,
                patchAdminDescriptiveTexts: ApiState.NOT_STARTED,
                getAdminDescriptiveTextbyId: ApiState.NOT_STARTED,
                getAdminVariations: ApiState.NOT_STARTED,
                saveAdminVariations: ApiState.NOT_STARTED,
                updateAdminVariations: ApiState.NOT_STARTED,
                getTaxNames: ApiState.NOT_STARTED,
                createTaxName: ApiState.NOT_STARTED,
                updateTaxName: ApiState.NOT_STARTED,
                createDescriptiveText: ApiState.NOT_STARTED,
                getUploadedVariationMappingReports: ApiState.NOT_STARTED,
                processVariationMappingFile: ApiState.NOT_STARTED,
                downloadVariationMappingErrorReport: ApiState.NOT_STARTED,
            };
        },
        resetVariationMappingFileState(state) {
            state.apiState.processVariationMappingFile = ApiState.NOT_STARTED;
        },
        resetDescriptiveTextState(state) {
            state.descriptiveText = '';
            state.descriptiveTextId = 0;
            state.descriptiveTextType = '0';
            state.showModal = false;
            state.isDelete = false;
            state.selectedIds = [];
            state.clearModalData = false;
            state.saveDescriptiveTexts = {
                saveData: { descriptiveTextId: [] },
            };
            state.updateDescriptiveTexts = {
                updateData: { message: '' },
            };
        },
        resetVariationState(state) {
            state.variationId = 0;
            state.variationName = '';
            state.variationDescription = '';
            state.saveVariations = {
                saveData: { variationIds: [] },
            };
            state.updateVariations = {
                updateData: { message: '' },
            };
        },
        resetCoreTaxAssignemntState(state) {
            state.variationsbyCountryandClass = initialVariation;
            state.variationsbyCoreTax = initialVariation;
            state.assignVariationsToInsuranceClasses = {
                responseData: { message: '' },
            };
            state.assignVariationsToCoreTax = {
                responseData: { message: '' },
            };
        },
    },
});

export const {
    setDescriptiveText,
    setDescriptiveTextId,
    setDescriptiveTextType,
    setDescriptiveTextCountry,
    setShowModal,
    setVariationEdit,
    setShowVariationModel,
    setIsDelete,
    setSelectedIds,
    setClearModalData,
    setVariationId,
    setVariationName,
    setVariationDescription,
    resetApiState,
    resetDescriptiveTextState,
    resetVariationState,
    setCoreTaxId,
    setLobPairId,
    setCoreTaxCountry,
    setClassesToMap,
    setCoreTaxTextTaxName,
    setCoreTaxTaxNameObjectToBeAdded,
    setCoreTaxCoInsuranceObjectToBeAdded,
    setCoreTaxTaxPointObjectToBeAdded,
    resetCoreTaxTaxNameObjectToBeAdded,
    resetCoreTaxCoInsuranceObjectToBeAdded,
    resetCoreTaxTaxPointObjectToBeAdded,
    resetDescriptiveTextIDs,
    setCoreTaxNewOrEdit,
    setCoreTaxClone,
    setNewOrEditCoreTaxId,
    setCloneCoreTaxId,
    resetCoreTaxAssignemntState,
    setCoreTaxReferenceLegislationObjectToBeAdded,
    resetCoreTaxReferenceLegislationObjectToBeAdded,
    resetVariationMappingFileState,
} = adminSlice.actions;

export default adminSlice.reducer;
